/**
 * The front door, built to `proto/Login.dc.html` (export 17; James, Sep 25 — "sign-in is not the
 * design and never was": drawn literally, and scored by a pixel diff against the drawing, under
 * 5% at 1440 and 390. The "score a poster by its elements" rule from Walk fixes 1 is withdrawn).
 *
 * Two rules of the canvas, both James's (Sep 25) and both in CLAUDE.md:
 *   · **A drawn frame is never rendered.** The drawing's cream margin and its 920-wide card edge
 *     are the artboard. The page fills the screen: the navy panel runs full height on the left,
 *     the door fills the right half, and on a phone the navy band is on top with the door below.
 *   · **A poster's states render one at a time.** `Login.dc.html` draws three: the sign-in card,
 *     the first-time card and the code card. Here the sign-in card is the door; the first-time
 *     card takes its place when an invite link opened the page; the code card takes its place
 *     after Forgot password. Never two at once, never one beneath another.
 *
 * The lanes are the app's own, unchanged: email and password here; a link in the email for
 * volunteers, families and sponsors; a token and a PIN for a booth; a passkey as the second step
 * for the people who approve money.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api, isRefused, sentence } from '../../shell/api.js';
import type { Route } from '../../shell/router.js';
import './login.css';

export interface LoginProps {
  route: Route;
  onSignedIn: () => Promise<void>;
}

/** Which lane the door is showing. The drawing's four, in its order. */
type Lane = 'password' | 'passkey' | 'link' | 'booth';
const LANES: { id: Lane; label: string }[] = [
  { id: 'password', label: 'PASSWORD' },
  { id: 'passkey', label: 'PASSKEY' },
  { id: 'link', label: 'A LINK IN THE EMAIL' },
  { id: 'booth', label: 'BOOTH PIN' },
];

/** Where a sign-in lands. The drawing puts the choice on the door and remembers it. */
type Door = 'app' | 'site';
const DOOR_KEY = 'cider-door';
export function doorChoice(): Door {
  try {
    return window.localStorage.getItem(DOOR_KEY) === 'site' ? 'site' : 'app';
  } catch {
    return 'app';
  }
}

/** The parish's own number — the one a printed page and this page both give (`PARISH.phone`). */
export const PARISH_PHONE = '(586) 731-5347';

/** The wordmark and the fireworks, from the design system's own files. */
const MARK = new URL(
  '../../../../../packages/ui/assets/logo-white-transparent.png',
  import.meta.url,
).href;
const FIREWORKS = new URL('../../../../../packages/ui/assets/fireworks-white.png', import.meta.url)
  .href;

/** The lane each address opens; the door's own four, as the drawing names them. */
function laneOfHash(): Lane {
  const h = typeof window === 'undefined' ? '' : window.location.hash;
  return h === '#passkey'
    ? 'passkey'
    : h === '#link'
      ? 'link'
      : h === '#booth'
        ? 'booth'
        : 'password';
}

export function Login({ route, onSignedIn }: LoginProps): ReactNode {
  const [lane, setLane] = useState<Lane>(laneOfHash);
  const [door, setDoor] = useState<Door>(doorChoice);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [deviceToken, setDeviceToken] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  /** The code card is a state of this page, not a card under it: Forgot password opens it. */
  const [sentCodeTo, setSentCodeTo] = useState<string | null>(null);
  /** Who the invite is for, so the card greets them by name as the drawing does. */
  const [invitedName, setInvitedName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** The address a link was just asked for: the lane then reads as the drawing's sent state. */
  const [sentTo, setSentTo] = useState<string | null>(null);

  // An address is a way in: /login#link opens that lane, whether the page is new or already open.
  useEffect(() => {
    const onHash = (): void => {
      setLane(laneOfHash());
      setRefusal(null);
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  // An invite: the card greets whoever the email went to. A dead token simply has no name.
  useEffect(() => {
    if (route.lane !== 'invite' || !route.token) return;
    api<{ firstName: string | null }>('GET', `/auth/invite/${route.token}`)
      .then((r) => {
        setInvitedName(r.firstName);
      })
      .catch(() => {
        setInvitedName(null);
      });
  }, [route.lane, route.token]);

  // An arrival: the link in the email signs the person in on this device.
  useEffect(() => {
    if (route.lane === 'link' && route.token) {
      setBusy(true);
      api('GET', `/auth/magic-link/${route.token}`)
        .then(onSignedIn)
        .catch((e: unknown) => {
          setRefusal(sentence(e));
        })
        .finally(() => {
          setBusy(false);
        });
    }
  }, [route.lane, route.token, onSignedIn]);

  const run = async (call: () => Promise<unknown>, after?: () => void): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    setNote(null);
    try {
      await call();
      if (after) after();
      else await onSignedIn();
    } catch (e) {
      setRefusal(sentence(e));
    } finally {
      setBusy(false);
    }
  };

  const pickDoor = (d: Door): void => {
    try {
      window.localStorage.setItem(DOOR_KEY, d);
    } catch {
      // A browser that refuses storage still opens the door; it just forgets the choice.
    }
    setDoor(d);
  };

  /**
   * What is actually in the boxes, not what React last heard. A password manager fills a field
   * without an input event, so a door that trusts its own state signs in with nothing and the
   * person is told their correct password does not match (James, on alpha.39). The field is the
   * truth; the state is only what the page draws.
   */
  const typed = (id: string, held: string): string => {
    const el = document.getElementById(id);
    return el instanceof HTMLInputElement ? el.value : held;
  };
  /** The form that is open, by its own id — the one thing a lane's submit reads. */
  const formOf = (id: string): HTMLFormElement | null => {
    const el = document.getElementById(id);
    return el instanceof HTMLFormElement ? el : null;
  };
  /**
   * The password lane's own submit. Everything it sends comes out of the form, at the moment the
   * person pressed — never out of a closure, a click handler's copy or the page's memory. The
   * doors submit this form rather than call it, so there is one path in and one thing sent.
   */
  const signInFrom = (form: HTMLFormElement): void => {
    const data = new FormData(form);
    const value = (name: string): string => {
      const v = data.get(name);
      return typeof v === 'string' ? v : '';
    };
    const address = value('email').trim();
    const secret = value('password');
    if (!address) {
      setRefusal('Enter your email address.');
      document.getElementById('email')?.focus();
      return;
    }
    if (!secret) {
      setRefusal('Enter your password.');
      document.getElementById('pw')?.focus();
      return;
    }
    void run(() =>
      api('POST', '/auth/sign-in', { email: address, password: secret }).catch((e: unknown) => {
        // The address it tried, in the refusal: a door that will not open should say what it
        // knocked on (James, on alpha.40 — he could not tell what the form had sent).
        throw isRefused(e) && e.refusal.error === 'wrong_sign_in'
          ? new Error(`${sentence(e)} The address it tried was ${address}.`)
          : e;
      }),
    );
  };
  const signIn = (): void => {
    const form = formOf('login-password');
    if (form) form.requestSubmit();
  };
  const sendLink = (): void =>
    void run(
      () =>
        api<{ reason: string }>('POST', '/auth/magic-link', {
          email: typed('email2', email),
        }).then(() => {
          setSentTo(typed('email2', email));
        }),
      () => undefined,
    );
  /** Lane 2: the browser offers the passkeys it holds for this site; the account is the one it picks. */
  const passkeySignIn = (): void =>
    void run(async () => {
      const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>(
        'POST',
        '/auth/passkey/begin',
      );
      const answer = await startAuthentication({ optionsJSON });
      await api('POST', '/auth/passkey/finish', answer as unknown as Record<string, unknown>);
    });
  const openDevice = (): void =>
    void run(() =>
      api('POST', '/auth/device', {
        deviceToken: typed('dt', deviceToken),
        pin: typed('pin', pin),
      }),
    );
  const invite = route.lane === 'invite' && route.token ? route.token : null;
  /**
   * **Your account, with a passkey** (James, Oct 2): the packet's first item is "set a password
   * or passkey". The link is the credential until the passkey is made on this device.
   */
  const setUpWithPasskey = (): void =>
    void run(async () => {
      if (!invite) return;
      const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>(
        'POST',
        `/auth/invite/${invite}/passkey/begin`,
      );
      const response = await startRegistration({ optionsJSON });
      await api('POST', `/auth/invite/${invite}/passkey/finish`, {
        response: response as unknown as Record<string, unknown>,
      });
    });

  /** Forgot password: the code goes to the address already typed, and the card takes the door. */
  const forgot = (): void => {
    const to = typed('email', email).trim();
    if (!to) {
      setRefusal('Type your email above first, and the code goes to it.');
      document.getElementById('email')?.focus();
      return;
    }
    void run(
      () => api('POST', '/auth/forgot', { email: to }),
      () => {
        setSentCodeTo(to);
        setCode('');
      },
    );
  };
  const enterCode = (six: string): void => {
    if (!sentCodeTo) return;
    void run(() => api('POST', '/auth/forgot/code', { email: sentCodeTo, code: six })).then(() => {
      // A refused code empties the boxes and gives them back: typing over a full code would
      // otherwise spend a try on every keystroke.
      setCode((held) => (held === six ? '' : held));
      document.getElementById('code0')?.focus();
    });
  };

  /** Which of the poster's three states this page is showing. One, always. */
  const state: 'door' | 'invite' | 'code' = invite ? 'invite' : sentCodeTo ? 'code' : 'door';

  return (
    <div className="login">
      <div className="login__card">
        <LoginPanel />
        <section className="login__right">
          {state === 'door' ? (
            <>
              <p className="login__lanes">
                {LANES.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    className={`login__lane${lane === l.id ? ' login__lane--on' : ''}`}
                    aria-pressed={lane === l.id}
                    onClick={() => {
                      setLane(l.id);
                      setRefusal(null);
                      setNote(null);
                      setSentTo(null);
                      // Each lane has its own address, so a person can be sent to the one they need.
                      window.history.replaceState(null, '', `#${l.id}`);
                    }}
                  >
                    {l.label}
                  </button>
                ))}
              </p>

              {lane === 'password' ? (
                <form
                  className="login__lane-body"
                  id="login-password"
                  // The door says what is missing in its own words; the browser's own bubble
                  // would say it in the browser's, and differently in each one.
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    signInFrom(e.currentTarget);
                  }}
                >
                  <div>
                    <h1 className="login__head">Welcome back</h1>
                    <p className="login__charter">Sign in with your email and password.</p>
                  </div>
                  <div className="login__field">
                    <label className="login__label" htmlFor="email">
                      Email
                    </label>
                    <input
                      className="login__input"
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                    />
                  </div>
                  <div className="login__field">
                    <label className="login__label" htmlFor="pw">
                      Password
                    </label>
                    <input
                      className="login__input"
                      id="pw"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                    />
                  </div>
                  {refusal ? (
                    <p className="login__refusal" role="alert">
                      {refusal}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    className="login__submit"
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                </form>
              ) : null}

              {lane === 'passkey' ? (
                <div className="login__lane-body">
                  <div>
                    <h1 className="login__head">Use your passkey</h1>
                    <p className="login__charter">
                      Your phone or laptop asks for your face, fingerprint or screen lock. Nothing
                      to type.
                    </p>
                  </div>
                  {/*
                    The drawing's passkey box (Login.dc.html, PASSKEY): the device's own passkey,
                    which this page cannot name before it is used — so the box says what it is
                    and is the button that asks for it.
                  */}
                  <button
                    type="button"
                    className="login__passkey"
                    disabled={busy}
                    onClick={passkeySignIn}
                  >
                    <svg
                      className="login__passkey-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
                      <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
                      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
                      <path d="M2 12a10 10 0 0 1 18-6" />
                      <path d="M2 16h.01" />
                      <path d="M21.8 16c.2-2 .131-5.354 0-6" />
                      <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
                      <path d="M8.65 22c.21-.66.45-1.32.57-2" />
                      <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
                    </svg>
                    <span>
                      <b>Use this device’s passkey</b>
                      <span className="login__passkey-sub">
                        your face, fingerprint or screen lock
                      </span>
                    </span>
                  </button>
                  {refusal ? (
                    <p className="login__refusal" role="alert">
                      {refusal}
                    </p>
                  ) : null}
                  <p className="login__fine">
                    Directors and the finance director need this second step because they approve
                    money and sign checks. Everybody else can skip it.
                  </p>
                </div>
              ) : null}

              {lane === 'link' ? (
                <form
                  className="login__lane-body"
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendLink();
                  }}
                >
                  <div>
                    <h1 className="login__head">Check your email</h1>
                    {sentTo ? (
                      // Never "we sent it" outright: whether the address is ours is not said.
                      <p className="login__charter" role="status">
                        If <b>{sentTo}</b> is an address we know, a sign-in link is on its way. It
                        works once and for fifteen minutes.
                      </p>
                    ) : (
                      <p className="login__charter">
                        We send a sign-in link to the address the festival already holds. It works
                        once and for fifteen minutes.
                      </p>
                    )}
                  </div>
                  <div className="login__field" hidden={sentTo !== null}>
                    <label className="login__label" htmlFor="email2">
                      Email
                    </label>
                    <input
                      className="login__input"
                      id="email2"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                    />
                  </div>
                  <p className="login__aside-box login__aside-box--flat">
                    For volunteers, families and sponsors. The link opens <b>My Shifts</b> or your
                    family’s page — there’s no password to remember.
                  </p>
                  {note ? <p role="status">{note}</p> : null}
                  {refusal ? (
                    <p className="login__refusal" role="alert">
                      {refusal}
                    </p>
                  ) : null}
                  {/* This lane's own one action; no doors — the link lands where the person belongs. */}
                  <button
                    type="submit"
                    className={sentTo ? 'login__again' : 'login__go'}
                    disabled={busy}
                  >
                    {sentTo ? 'Send it again' : 'Send me a link'}
                  </button>
                </form>
              ) : null}

              {lane === 'booth' ? (
                <form
                  className="login__lane-body"
                  onSubmit={(e) => {
                    e.preventDefault();
                    openDevice();
                  }}
                >
                  <div>
                    <h1 className="login__head">A booth, a count room or a till</h1>
                    <p className="login__charter">
                      Enter this device’s token and PIN to open check-in.
                    </p>
                  </div>
                  <div className="login__field">
                    <label className="login__label" htmlFor="dt">
                      Device token
                    </label>
                    <input
                      className="login__input"
                      id="dt"
                      type="password"
                      autoComplete="off"
                      required
                      value={deviceToken}
                      onChange={(e) => {
                        setDeviceToken(e.target.value);
                      }}
                    />
                  </div>
                  <div className="login__field">
                    <label className="login__label" htmlFor="pin">
                      PIN
                    </label>
                    <input
                      className="login__input"
                      id="pin"
                      type="password"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      minLength={4}
                      maxLength={12}
                      value={pin}
                      onChange={(e) => {
                        setPin(e.target.value);
                      }}
                    />
                  </div>
                  <p className="login__fine">
                    Three wrong tries locks this device for fifteen minutes — only this one. A
                    volunteer chair can unlock it from her phone.
                  </p>
                  {refusal ? (
                    <p className="login__refusal" role="alert">
                      {refusal}
                    </p>
                  ) : null}
                  {/* This lane's own one action; a booth is a device, not a person. */}
                  <button type="submit" className="login__go" disabled={busy}>
                    Open check-in
                  </button>
                </form>
              ) : null}

              {/*
                The two doors belong to the lanes that sign a person in — Password and Passkey
                (James, Sep 25). A link and a booth PIN carry their own one action instead: a
                lane never shows a button that is not its own.
              */}
              {lane === 'password' || lane === 'passkey' ? (
                <div className="login__doors">
                  {/*
                    The colors are the drawing's and they do not move: the app is the red one and
                    the full site is the outlined one (James, on alpha.40). What the device
                    remembers is said in the sentence, not painted on the buttons.
                  */}
                  <button
                    type="button"
                    className="login__door login__door--on"
                    disabled={busy}
                    onClick={() => {
                      pickDoor('app');
                      if (lane === 'passkey') passkeySignIn();
                      else signIn();
                    }}
                  >
                    <span>Sign in → the app</span>
                    <span className="login__door-sub">your area, on your phone</span>
                  </button>
                  <button
                    type="button"
                    className="login__door"
                    disabled={busy}
                    onClick={() => {
                      pickDoor('site');
                      if (lane === 'passkey') passkeySignIn();
                      else signIn();
                    }}
                  >
                    <span>Sign in → the full site</span>
                    <span className="login__door-sub">every view</span>
                  </button>
                  <p className="login__door-note">
                    {door === 'app'
                      ? 'The app is where most people start. This device remembers your choice.'
                      : 'This device remembers the full site — press it and you land there; the app is one tap away.'}
                  </p>
                </div>
              ) : null}

              <p className="login__links">
                <button type="button" className="login__link" onClick={forgot}>
                  Forgot password?
                </button>
                <button
                  type="button"
                  className="login__link"
                  onClick={() => {
                    setNote(
                      `Your packet email carries the link that sets up your account — open it on the device you want to use. The directors can re-send one: ${PARISH_PHONE}.`,
                    );
                  }}
                >
                  First time? Check your invite
                </button>
                <button
                  type="button"
                  className="login__link"
                  onClick={() => {
                    setNote(
                      `Call the volunteer chairs on ${PARISH_PHONE} — they can re-send an invite, clear a second factor, or open a booth device.`,
                    );
                    // The address appears when it is asked for, never on load.
                    window.history.replaceState(null, '', '#trouble');
                  }}
                >
                  Trouble signing in?
                </button>
              </p>
              {note && lane !== 'link' ? (
                <p className="login__fine" id="trouble" role="status">
                  {note}
                </p>
              ) : null}
            </>
          ) : null}

          {state === 'invite' ? (
            <section className="login__card2 login__card2--invite" id="invite">
              {/*
                **Set up your account** (James, Oct 2): the packet's first item, reached from the
                packet mail after a yes — not from the invitation, which is a question now.
              */}
              <div className="login__eyebrow">YOUR PACKET · ITEM 1 — YOUR ACCOUNT</div>
              <h2>
                {invitedName
                  ? `Welcome, ${invitedName} — set up your account`
                  : 'Set up your account'}
              </h2>
              <p>A passkey or one password, your own email — that’s the whole setup.</p>
              <form
                className="login__card2-body"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(() => api('POST', `/auth/invite/${invite}`, { password }));
                }}
              >
                <input
                  className="login__input"
                  id="newpw"
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password"
                  aria-label="New password"
                  minLength={12}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
                <input
                  className="login__input"
                  id="newpw2"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Repeat it"
                  aria-label="Repeat it"
                />
                <p className="login__door-note">
                  Twelve or more characters. A sentence works: <i>bakery sells out by two</i>
                </p>
                {refusal ? (
                  <p className="login__refusal" role="alert">
                    {refusal}
                  </p>
                ) : null}
                <button className="login__go" type="submit" disabled={busy}>
                  Set password &amp; open the app
                </button>
                <button
                  type="button"
                  className="login__link"
                  disabled={busy}
                  onClick={setUpWithPasskey}
                >
                  Use a passkey instead
                </button>
              </form>
              <p className="login__ms-line">
                This link works once and lasts two weeks. The directors can always re-send one.
              </p>
            </section>
          ) : null}

          {state === 'code' ? (
            <section className="login__card2" id="forgot">
              <div className="login__eyebrow">FORGOT PASSWORD — A CODE BY EMAIL</div>
              <h2>We emailed you a 6-digit code</h2>
              <p>Sent to {sentCodeTo}. Enter it, and it opens the app.</p>
              <div className="login__codes">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    className="login__code"
                    inputMode="numeric"
                    maxLength={1}
                    aria-label={`Code, digit ${String(i + 1)}`}
                    value={code[i] ?? ''}
                    onChange={(e) => {
                      const digit = e.target.value.replace(/\D/g, '').slice(-1);
                      const next = code.padEnd(6, ' ').split('');
                      next[i] = digit || ' ';
                      const six = next.join('');
                      setCode(six.trimEnd());
                      if (digit) {
                        const box = document.getElementById(`code${String(i + 1)}`);
                        box?.focus();
                        // The sixth digit is the whole code: nothing else to press.
                        if (!six.includes(' ')) enterCode(six);
                      }
                    }}
                    id={`code${String(i)}`}
                  />
                ))}
              </div>
              {refusal ? (
                <p className="login__refusal" role="alert">
                  {refusal}
                </p>
              ) : null}
              <p className="login__fine">
                Codes expire after 10 minutes; the newest email wins. Three wrong tries pauses
                things for 15 minutes — call {PARISH_PHONE} and the volunteer chairs will let you
                in.
              </p>
            </section>
          ) : null}

          {state !== 'door' ? (
            <p className="login__back">
              <button
                type="button"
                className="login__link"
                onClick={() => {
                  setSentCodeTo(null);
                  setRefusal(null);
                  if (invite) window.location.assign('/login');
                }}
              >
                Back to sign in
              </button>
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
}

/** The navy panel: the wordmark, the fireworks and the line about people. */
function LoginPanel(): ReactNode {
  return (
    <aside className="login__left">
      <img className="login__fireworks" src={FIREWORKS} alt="" />
      <div className="login__brand">
        <img className="login__mark" src={MARK} alt="Apple Fest" />
        <div>
          <span className="login__brand-name">CIDER</span>
          <span className="login__brand-sub">FESTIVAL OPERATIONS</span>
        </div>
      </div>
      <div className="login__say">
        <p className="login__line">The festival runs on people like you.</p>
        <p className="login__sub">One account for chairs, directors and the volunteer chairs.</p>
      </div>
    </aside>
  );
}

/**
 * The frame the other doorway screens sit in — the second step, the first sign-in's card — so
 * they keep the door's navy panel and its card.
 */
export function LoginFrame({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="login">
      <div className="login__card">
        <LoginPanel />
        <section className="login__right">{children}</section>
      </div>
    </div>
  );
}
